import { useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import InventoryAssetTile from '@atom/components/common/inventoryAssetTree/InventoryAssetTile';
import InventoryAssetTree from '@atom/components/common/inventoryAssetTree/InventoryAssetTree';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { usePreferences } from '@atom/hooks/usePreferences';
import {
  DatePicker,
  DateTimePicker,
  Icon,
  IconButton,
  Switch,
  TextField,
} from '@atom/mui';
import { ArrowBackIcon, CategoryIcon, EventIcon } from '@atom/mui/icons';
import { SearchAsset } from '@atom/types/asset';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import { getUpdatedOtherDate } from '@atom/utilities/timeUtilities';
import {
  isWorkDateEditingDisabled,
  showWorkDateTime,
} from '@atom/utilities/workPreferenceUtilities';

import CreateWorkModalContext from '../CreateWorkModalContext';
import {
  getAssetRequirement,
  getStartAndDueDateInitialValues,
  getTasksForAssetChange,
} from '../createWorkModalUtilities';

import CustomFields from './workDetails/CustomFields';
import SummarySection from './workDetails/SummarySection';

import classes from './steps.module.css';

const styles = {
  icon: {
    marginRight: '2rem',
  },
};

const WorkDetailsStep = () => {
  const { draft, setDraft, updateDraftField, assetLocked } = useContext(
    CreateWorkModalContext,
  );
  const preferences = usePreferences();
  const appointmentsEnabled = draft.workTemplate?.appointmentsEnabled ?? false;
  const dueDateTimeEnabled = showWorkDateTime(draft.workTemplate);
  const dueDateLocked = isWorkDateEditingDisabled(draft.workTemplate);
  const workNamingDisabled =
    preferences.workOrders?.workNamingDisabled ?? false;

  const [showAssetTree, setShowAssetTree] = useState<boolean>(false);
  const { showAssetAndLocation, requireAsset } = getAssetRequirement(
    draft.workTemplate,
  );

  useEffect(() => {
    const hasCalendarSeed = !!draft.fromCalendarDate || !!draft.fromCalendarGap;

    if (appointmentsEnabled) {
      if (draft.plannedStartDate) {
        return;
      }

      const values = getStartAndDueDateInitialValues({
        fromCalendarDate: draft.fromCalendarDate,
        fromCalendarGap: draft.fromCalendarGap,
      });

      setDraft(prev => ({
        ...prev,
        plannedStartDate: values.plannedStartDate,
        dueDate: values.dueDate,
      }));
    } else if (dueDateLocked) {
      setDraft(prev => ({
        ...prev,
        dueDate: null,
      }));
    } else if (!draft.dueDate) {
      setDraft(prev => ({
        ...prev,
        dueDate: hasCalendarSeed
          ? draft.fromCalendarDate || new Date(draft.fromCalendarGap.dueDate)
          : new Date(),
      }));
    }
  }, [draft.workTemplate]);

  const handleAssetClick = (
    assetData: InventoryAssetDetailType | SearchAsset,
  ) => {
    setDraft(prev => ({
      ...prev,
      asset: assetData,
      assetId: assetData?.id ?? null,
      isLocationBased: !R.isNil(assetData?.locationType),
      tasks: getTasksForAssetChange(prev, assetData?.id ?? null),
    }));
    setShowAssetTree(false);
  };

  const handleAssetRemove = () => {
    setDraft(prev => ({
      ...prev,
      asset: null,
      assetId: null,
      isLocationBased: false,
      tasks: getTasksForAssetChange(prev, null),
    }));
  };

  const errorDateBoundary =
    appointmentsEnabled &&
    draft.plannedStartDate?.valueOf() > draft.dueDate?.valueOf();

  const handlePlannedStartDateChange = (date?: Date) => {
    if (!date || date.toString() === 'Invalid Date') {
      return;
    }

    setDraft(prev => ({
      ...prev,
      plannedStartDate: date,
      dueDate: prev.dueDate
        ? getUpdatedOtherDate(date, prev.dueDate)
        : prev.dueDate,
    }));
  };

  const handleDueDateTimeChange = (date?: Date) => {
    if (!date || date.toString() === 'Invalid Date') {
      return;
    }

    setDraft(prev => ({
      ...prev,
      dueDate: date,
      plannedStartDate: prev.plannedStartDate
        ? getUpdatedOtherDate(date, prev.plannedStartDate)
        : prev.plannedStartDate,
    }));
  };

  if (showAssetTree) {
    return (
      <div className={classes.assetTree}>
        <div className={classes.assetTreeHeader}>
          <IconButton
            edge="start"
            size="small"
            onClick={() => setShowAssetTree(false)}
          >
            <Icon icon={ArrowBackIcon} />
          </IconButton>
          <span>Add Asset</span>
        </div>
        <div className={classes.assetTreeBody}>
          <InventoryAssetTree
            search="assets"
            schemaId={draft.workTemplate?.schemaId}
            includeArchived={false}
            onAssetClick={handleAssetClick}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classes.detailsHeader}>Work Details</div>
      <div className={classes.detailsContent}>
        <div className={clsx(classes.detailsRow, classes.name)}>
          <Icon style={styles.icon}>
            <img src={renameIcon} />
          </Icon>
          <TextField
            required
            fullWidth
            id="workOrderName"
            label="Work Name"
            value={draft.name}
            onChange={event => updateDraftField('name', event.target.value)}
            disabled={workNamingDisabled}
            data-cy="createWorkModalNameInput"
          />
        </div>
        {appointmentsEnabled ? (
          <>
            <div className={clsx(classes.detailsRow, classes.dueDate)}>
              <Icon icon={EventIcon} style={styles.icon} />
              <DateTimePicker
                key="plannedStartDate"
                label="Planned Start Date *"
                value={draft.plannedStartDate}
                onChange={handlePlannedStartDateChange}
                hideClear
                data-cy="createWorkModalPlannedStartDateInput"
              />
            </div>
            <div className={clsx(classes.detailsRow, classes.dueDate)}>
              <div className={classes.iconSpacer} />
              <DateTimePicker
                key="dueDate"
                label="Due Date *"
                value={draft.dueDate}
                onChange={handleDueDateTimeChange}
                error={errorDateBoundary}
                helperText={
                  errorDateBoundary
                    ? 'Due date cannot be before planned start date.'
                    : undefined
                }
                hideClear
                data-cy="createWorkModalDueDateInput"
              />
            </div>
          </>
        ) : dueDateLocked ? null : (
          <div className={clsx(classes.detailsRow, classes.dueDate)}>
            <Icon icon={EventIcon} style={styles.icon} />
            {dueDateTimeEnabled ? (
              <DateTimePicker
                label="Due Date"
                value={draft.dueDate}
                onChange={date => updateDraftField('dueDate', date || null)}
                data-cy="createWorkModalDueDateInput"
              />
            ) : (
              <DatePicker
                label="Due Date"
                value={draft.dueDate}
                onChange={date => updateDraftField('dueDate', date || null)}
                data-cy="createWorkModalDueDateInput"
              />
            )}
          </div>
        )}
        {showAssetAndLocation && (
          <>
            <div className={classes.detailsRow}>
              <Icon icon={CategoryIcon} style={styles.icon} />
              <InventoryAssetTile
                asset={draft.asset}
                onClick={assetLocked ? () => {} : () => setShowAssetTree(true)}
                onRemove={assetLocked ? () => {} : handleAssetRemove}
                assetRemovalDisabled={assetLocked}
                requireAsset={requireAsset}
                data-cy="createWorkModalAddAssetButton"
              />
            </div>
            <div className={clsx(classes.detailsRow, classes.location)}>
              <Switch
                checked={draft.isLocationBased}
                onChange={() =>
                  updateDraftField('isLocationBased', !draft.isLocationBased)
                }
                name="isLocationBased"
                label="Location"
                disabled={!!draft.asset}
                data-cy="createWorkModalLocationSwitch"
              />
            </div>
          </>
        )}
      </div>
      <SummarySection />
      <CustomFields />
    </>
  );
};

export default WorkDetailsStep;
